import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import { WorkoutAssignmentService } from '../../services';
import type { FupStoredAnswers } from '../../types/cycleObservations';
import {
  EMPTY_FUP_QUESTIONNAIRE,
  parseStoredFupAnswers,
  validateFupQuestionnaire,
  type FupQuestionnaireAnswers,
  type FupQuestionnaireErrors,
} from '../Fup/fupQuestionnaire';
import FupResponseModal from './FupResponseModal';

export type ManagedFupDetails = {
  studentId: number;
  studentFupId: number;
  assignmentId: number | null;
  studentName?: string | null;
  createdAt?: string | null;
  response?: string | null;
  answers?: FupStoredAnswers | null;
  notesSummary?: string | null;
};

type FupDecision = 'edit' | 'remove';

export type ManagedFupSaveResult = {
  studentFupId: number;
  response: string;
  answers: FupStoredAnswers | null;
};

export type ManagedFupDeleteResult = {
  studentFupId: number;
  assignmentId: number | null;
};

interface ManagedFupResponseModalProps {
  fup: ManagedFupDetails | null;
  canManage?: boolean;
  onClose: () => void;
  onSaved?: (result: ManagedFupSaveResult) => void;
  onDeleted?: (result: ManagedFupDeleteResult) => void;
}

const ManagedFupResponseModal: React.FC<ManagedFupResponseModalProps> = ({
  fup,
  canManage = false,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const [decision, setDecision] = useState<FupDecision | null>(null);
  const [editing, setEditing] = useState(false);
  const [answers, setAnswers] = useState<FupQuestionnaireAnswers | null>(null);
  const [answerErrors, setAnswerErrors] = useState<FupQuestionnaireErrors>({});
  const [responseDraft, setResponseDraft] = useState('');
  const [responseError, setResponseError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [response, setResponse] = useState<string | null>(null);
  const [storedAnswers, setStoredAnswers] = useState<FupStoredAnswers | null>(null);
  const [notesSummary, setNotesSummary] = useState<string | null | undefined>(undefined);
  const [fupSummary, setFupSummary] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null | undefined>(undefined);
  const [anamnesisExerciseHistory, setAnamnesisExerciseHistory] = useState<string | null>(null);
  const [anamnesisGoalDetails, setAnamnesisGoalDetails] = useState<string | null>(null);
  const [anamnesisAiSummary, setAnamnesisAiSummary] = useState<string | null>(null);
  const fupRef = useRef(fup);
  fupRef.current = fup;
  const fupKey = fup
    ? `${fup.studentId}:${fup.studentFupId}:${fup.assignmentId ?? ''}:${fup.createdAt ?? ''}`
    : '';

  useEffect(() => {
    const current = fupRef.current;
    if (!current) {
      return;
    }

    setDecision(null);
    setEditing(false);
    setAnswers(null);
    setAnswerErrors({});
    setResponseDraft('');
    setResponseError('');
    setLoadError(null);
    setNotesSummary(current.notesSummary);
    setCreatedAt(current.createdAt);
    setAnamnesisExerciseHistory(null);
    setAnamnesisGoalDetails(null);
    setAnamnesisAiSummary(null);
    setFupSummary(null);

    if (current.response) {
      setResponse(current.response);
      setStoredAnswers(current.answers ?? null);
      setLoadingDetails(false);
      return;
    }

    if (current.studentFupId <= 0) {
      setResponse(null);
      setStoredAnswers(null);
      setLoadingDetails(false);
      return;
    }

    let cancelled = false;
    setLoadingDetails(true);
    setResponse(null);
    setStoredAnswers(null);

    void WorkoutAssignmentService.getCycleObservations({
      studentId: current.studentId,
      studentFupId: current.studentFupId,
      assignmentId: current.assignmentId,
    }).then((observations) => {
      if (cancelled) {
        return;
      }

      setResponse(observations.fup_response);
      setStoredAnswers(observations.fup_answers);
      setFupSummary(observations.fup_summary);
      setAnamnesisExerciseHistory(observations.anamnesis_exercise_history);
      setAnamnesisGoalDetails(observations.anamnesis_goal_details);
      setAnamnesisAiSummary(observations.anamnesis_ai_summary);
      if (!current.createdAt && observations.fup_created_at) {
        setCreatedAt(observations.fup_created_at);
      }
    }).catch((error) => {
      if (!cancelled) {
        setLoadError(error instanceof Error ? error.message : 'Não foi possível carregar as observações.');
      }
    }).finally(() => {
      if (!cancelled) {
        setLoadingDetails(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fupKey]);

  const close = useCallback(() => {
    setDecision(null);
    setEditing(false);
    onClose();
  }, [onClose]);

  const confirmEdit = useCallback(() => {
    if (!fup || decision !== 'edit') {
      return;
    }

    const parsedAnswers = parseStoredFupAnswers(storedAnswers, response);
    setAnswers(parsedAnswers);
    setAnswerErrors({});
    setResponseDraft(response ?? '');
    setResponseError('');
    setEditing(true);
    setDecision(null);
  }, [decision, fup, storedAnswers, response]);

  const handleAnswerChange = useCallback(<K extends keyof FupQuestionnaireAnswers>(
    field: K,
    value: FupQuestionnaireAnswers[K],
  ) => {
    setAnswers((current) => ({
      ...(current ?? EMPTY_FUP_QUESTIONNAIRE),
      [field]: value,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!fup) {
      return;
    }

    if (answers) {
      const errors = validateFupQuestionnaire(answers);
      setAnswerErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }
    } else if (responseDraft.trim().length < 5) {
      setResponseError('Descreva com pelo menos 5 caracteres.');
      return;
    }

    setSaving(true);
    try {
      const result = await WorkoutAssignmentService.updateFupResponse({
        studentFupId: fup.studentFupId,
        ...(answers
          ? { answers }
          : { response: responseDraft.trim() }),
      });
      setResponse(result.response);
      setStoredAnswers(result.answers);
      setEditing(false);
      onSaved?.({
        studentFupId: fup.studentFupId,
        response: result.response,
        answers: result.answers,
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível editar o FUP.');
    } finally {
      setSaving(false);
    }
  }, [answers, fup, onSaved, responseDraft]);

  const handleRemove = useCallback(async () => {
    if (!fup) {
      return;
    }

    setSaving(true);
    try {
      await WorkoutAssignmentService.deleteFup(fup.studentFupId);
      const removed = {
        studentFupId: fup.studentFupId,
        assignmentId: fup.assignmentId,
      };
      setDecision(null);
      setEditing(false);
      onDeleted?.(removed);
      onClose();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível remover o FUP.');
    } finally {
      setSaving(false);
    }
  }, [fup, onClose, onDeleted]);

  const showManage = canManage && Boolean(fup && fup.studentFupId > 0);

  return (
    <>
      <FupResponseModal
        open={Boolean(fup)}
        studentName={fup?.studentName}
        createdAt={createdAt}
        response={response}
        storedAnswers={storedAnswers}
        notesSummary={notesSummary}
        fupSummary={fupSummary}
        anamnesisExerciseHistory={anamnesisExerciseHistory}
        anamnesisGoalDetails={anamnesisGoalDetails}
        anamnesisAiSummary={anamnesisAiSummary}
        loading={loadingDetails}
        loadError={loadError}
        editing={editing}
        questionnaire={answers}
        questionnaireErrors={answerErrors}
        onQuestionnaireChange={handleAnswerChange}
        responseDraft={responseDraft}
        responseError={responseError}
        onResponseDraftChange={(value) => {
          setResponseDraft(value);
          setResponseError('');
        }}
        saving={saving}
        onSave={handleSave}
        onCancelEdit={() => setEditing(false)}
        onEdit={showManage ? () => setDecision('edit') : undefined}
        onRemove={showManage ? () => setDecision('remove') : undefined}
        onClose={close}
      />

      <ConfirmModal
        open={decision === 'edit'}
        title="Editar FUP"
        message="Deseja mesmo editar as respostas deste FUP?"
        confirmLabel="Editar"
        cancelLabel="Cancelar"
        variant="info"
        onCancel={() => setDecision(null)}
        onConfirm={confirmEdit}
      />

      <ConfirmModal
        open={decision === 'remove'}
        title="Remover FUP"
        message="Deseja mesmo remover este FUP? As respostas deste ciclo serão apagadas e será possível copiar um novo link para enviar de novo."
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        loading={saving}
        onCancel={() => {
          if (!saving) {
            setDecision(null);
          }
        }}
        onConfirm={handleRemove}
      />
    </>
  );
};

export default ManagedFupResponseModal;
